// SPDX-License-Identifier: AGPL-3.0-or-later
// Copyright (C) 2026 Ben Richardson <hi@ben.gy>
// Additional terms apply under AGPL-3.0 section 7(b) — see ADDITIONAL-TERMS.md

/*
 * The three modals.
 *
 * Kept out of ui.ts because this is prose, and prose about what a tool can and
 * cannot do deserves to be read as prose rather than found between event
 * handlers. The Privacy modal in particular is a set of promises, several of
 * which scripts/gate.mjs checks against the shipped bundle at build time.
 */

import { term } from './glossary.ts';
import { MODEL_ID, MODEL_URL, TOTAL_BYTES, VISION_BYTES, TEXT_BYTES, TOKENIZER_BYTES } from './model/config.mjs';

const mb = (bytes: number) => `${(bytes / 1_000_000).toFixed(1)} MB`;

export const HOW_IT_WORKS = `
<ol class="steps">
  <li>
    <h3>It reads the file’s index, not the file</h3>
    <p>Video is stored as occasional whole frames — ${term('keyframe', 'keyframes')} — with the
    frames between them recorded only as differences. The container keeps a list of where those
    whole frames are, so cuepoint can tell you how many there are and how long a scan will take
    <em>before</em> decoding anything. A six-gigabyte file answers in under a second.</p>
  </li>
  <li>
    <h3>It samples, rather than watching everything</h3>
    <p>Looking at all 30 frames of every second would cost thirty times as much and tell you
    almost nothing extra. cuepoint builds a ladder of timestamps at the rate you choose, preferring
    the keyframes because they are nearly free to decode, and filling gaps only where the recorder
    left them wide.</p>
  </li>
  <li>
    <h3>Each sampled frame becomes 512 numbers</h3>
    <p>A small vision model — MobileCLIP — turns each frame into an ${term('embedding')}. Your typed
    query goes through the model’s other half and becomes 512 numbers of the same kind. Everything
    after this point is arithmetic on your own machine.</p>
  </li>
  <li>
    <h3>Every frame is compared with your query, live</h3>
    <p>A two-hour video sampled once a second is about fifteen megabytes of numbers, and comparing
    all of it takes a few milliseconds — which is why results move as you type rather than waiting
    behind a button. Scores are ${term('median', 'smoothed')} across neighbouring frames, then
    ${term('nms', 'nearby peaks are merged')} so one event is one result.</p>
  </li>
  <li>
    <h3>You leave with files</h3>
    <p>Each moment can be cut out as a real MP4, trimmed from the original with no re-encoding of
    anything you did not ask for. You also get a CSV of what was found, a WebVTT chapter track that
    VLC, Plex, Jellyfin and YouTube read natively, and the index itself so the same video is
    searchable next week without scanning it again.</p>
  </li>
</ol>
<p class="modal-note">The model is downloaded once (${mb(TOTAL_BYTES)}) and kept by your browser.
After that, cuepoint works with the network switched off.</p>
`;

export const LIMITS = `
<p><strong>cuepoint finds things, not events.</strong> The model looks at each frame on its own and
has no sense of time, so it can find a red car, a bicycle, a wet road at night or a person in
hi-vis — but not “a car <em>turning left</em>” or “someone <em>arriving</em> at the door”. Those
collapse to “a car” and “a person at a door”. ${term('scene', 'Describe what you would see in a single freeze-frame.')}</p>

<p><strong>It cannot tell you something is absent.</strong> The score it produces has no absolute
meaning — in our own measurements a correct best match scored anywhere between 0.11 and 0.27
depending only on the wording of the query. So cuepoint always returns its best guesses, ranked,
and shows you the ${term('baseline')} they are ranked against. If the top result is barely above
that line, the honest reading is “nothing here stood out”, not “it is not in the video”. There is no
setting that turns this into a yes-or-no answer, because no such answer exists.</p>

<p><strong>Small and distant things are hard.</strong> A car forty pixels wide in a wide dashcam
frame is a handful of details inside a picture that is mostly road, sky and bonnet, and the model
sees the picture. Things that fill a reasonable part of the frame are found reliably; things in the
far distance often are not.</p>

<p><strong>Continuous footage is harder than it looks.</strong> If every frame of an hour is the
same street from the same camera, all the scores sit close together and the ranking has less to work
with. The distribution shown under the results is there so you can see when that is happening.</p>
`;

export const PRIVACY = `
<h3>Protected</h3>
<ul>
  <li><strong>Your video never leaves this tab.</strong> It is opened straight from your disk and
  decoded by your browser. No frame, no thumbnail, no clip and no byte of the original is sent
  anywhere — there is no upload code in this application, and the build refuses to publish if any
  appears.</li>
  <li><strong>Your queries stay here too.</strong> They are turned into numbers by a model already
  on your machine. No query text is transmitted.</li>
  <li><strong>Nothing is written to disk.</strong> The index lives in memory and is only saved if
  you download it. cuepoint uses no databases, no private filesystem and no local storage, and the
  build checks that too.</li>
  <li><strong>It works offline.</strong> After the one model download, you can disconnect entirely
  and everything still works.</li>
</ul>

<h3>Not protected</h3>
<ul>
  <li><strong>The model download is visible to Hugging Face.</strong> Fetching
  ${mb(VISION_BYTES)} + ${mb(TEXT_BYTES)} + ${mb(TOKENIZER_BYTES)} of weights tells them that
  someone at your address downloaded MobileCLIP. It says nothing about your video or your queries,
  and it happens once.</li>
  <li><strong>Loading this page is visible</strong> to GitHub Pages and Cloudflare, as loading any
  page is.</li>
  <li><strong>An exported clip is an ordinary file.</strong> Once you send it to an insurer or a
  solicitor, it is theirs. cuepoint does not strip metadata from it.</li>
  <li><strong>cuepoint cannot tell you whether footage has been edited</strong>, who is in it, or
  whether anything is missing from it. It does not try.</li>
</ul>

<h3>Trust surface</h3>
<ul>
  <li>This page and its code, served by GitHub Pages over TLS.</li>
  <li><a href="${MODEL_URL}" target="_blank" rel="noopener">${MODEL_ID}</a> on Hugging Face, once,
  for the model weights. The runtime that executes them is served from this origin, not a CDN.</li>
  <li>A Cloudflare Web Analytics beacon records anonymous page views — no cookies, no
  fingerprinting, no cross-site tracking; your files and data are never sent to it.</li>
  <li>Feedback you choose to send is sent to feedback.benrichardson.dev. Nothing is sent unless you
  open the feedback form and press Send; your files and data never are.</li>
</ul>
`;

export const ABOUT = `
<p>cuepoint searches a video file you already have, by description, without uploading it — and
hands back the matching moments as clips you can send to somebody.</p>

<p>It exists because the footage people most need to search is the footage they are least able to
hand over. A dashcam loop shows your street and your neighbours; a doorbell clip shows other
people’s children. The tools that offer to search that material want a copy of it first, usually for
a monthly fee. This one runs the same class of model on your own machine and keeps nothing.</p>

<p>Built by <a href="https://benrichardson.dev/" target="_blank" rel="noopener">Ben Richardson</a>.
The rest of the catalogue is at
<a href="https://lab.benrichardson.dev" target="_blank" rel="noopener">lab.benrichardson.dev</a>,
and the source is at
<a href="https://github.com/ben-gy/cuepoint" target="_blank" rel="noopener">github.com/ben-gy/cuepoint</a>.</p>

<p class="modal-note">The demo reel is assembled from photographs on Wikimedia Commons that were
verified to be CC0 or public domain before use; the full list is in the repository at
<code>public/samples/CREDITS.txt</code>. Image understanding is by Apple’s MobileCLIP-S0, converted
to ONNX by Xenova.</p>
`;
